import { useCallback, useRef } from 'react';

const fontStr = (fontSize, fontWeight, fontFamily) => `${fontWeight} ${fontSize}px ${fontFamily}`;

export default function useTruncatedText({ ssrCharWidth = 0.6, maxCacheSize = 2000 } = {}) {
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const cacheRef = useRef(new Map());

  const getCtx = () => {
    if (typeof window === 'undefined') {
      return null;
    }

    if (!ctxRef.current) {
      canvasRef.current = document.createElement('canvas');
      ctxRef.current = canvasRef.current.getContext('2d');
    }

    return ctxRef.current;
  };

  const widthOf = useCallback(
    (text, fontSize, fontWeight, fontFamily) => {
      const ctx = getCtx();
      const font = fontStr(fontSize, fontWeight, fontFamily);
      const key = `${font}|${text}`;

      if (!ctx) {
        return text.length * (fontSize * ssrCharWidth);
      }

      if (cacheRef.current.has(key)) {
        return cacheRef.current.get(key);
      }

      ctx.font = font;
      const w = ctx.measureText(text).width;

      cacheRef.current.set(key, w);

      if (cacheRef.current.size > maxCacheSize) {
        const firstKey = cacheRef.current.keys().next().value;
        cacheRef.current.delete(firstKey);
      }

      return w;
    },
    [maxCacheSize, ssrCharWidth],
  );

  const getTruncatedText = useCallback(
    (words, maxWidth, options = {}) => {
      const {
        fontSize = 12,
        fontWeight = 400,
        fontFamily = '"Roboto", sans-serif',
        separator = ', ',
        moreFormat = n => `+${n}`,
      } = options;

      if (!Array.isArray(words) || words.length === 0 || typeof maxWidth !== 'number') {
        return { text: '', shown: 0, hidden: 0, isTruncated: false };
      }

      const join = arr => arr.join(separator);
      const measure = s => widthOf(s, fontSize, fontWeight, fontFamily);

      const picked = [];
      let currentWidth = 0;

      for (let i = 0; i < words.length; i += 1) {
        const nextPiece = picked.length ? separator + words[i] : words[i];
        const nextWidth = measure(nextPiece);

        if (currentWidth + nextWidth <= maxWidth) {
          picked.push(words[i]);
          currentWidth += nextWidth;
        } else {
          const remaining = words.length - picked.length;
          const token = (picked.length ? separator : '') + moreFormat(remaining);

          if (currentWidth + measure(token) <= maxWidth) {
            const text = (picked.length ? join(picked) + separator : '') + moreFormat(remaining);
            return { text, shown: picked.length, hidden: remaining, isTruncated: true };
          }

          while (picked.length > 0) {
            picked.pop();
            const base = picked.length ? join(picked) : '';
            const rest = words.length - picked.length;
            const tkn = (picked.length ? separator : '') + moreFormat(rest);

            if (measure(base) + measure(tkn) <= maxWidth) {
              const text = (picked.length ? base + separator : '') + moreFormat(rest);
              return { text, shown: picked.length, hidden: rest, isTruncated: true };
            }
          }

          const plusOnly = moreFormat(words.length);

          if (measure(plusOnly) <= maxWidth) {
            return { text: plusOnly, shown: 0, hidden: words.length, isTruncated: true };
          }

          return { text: '', shown: 0, hidden: words.length, isTruncated: true };
        }
      }

      const full = join(picked);
      return { text: full, shown: picked.length, hidden: 0, isTruncated: false };
    },
    [widthOf],
  );

  return getTruncatedText;
}
